import { useAtom } from 'jotai';
import { useQuery } from 'react-query';
import { useCompanies } from 'state/companies';
import { fetchGroup, groupAtom, useMercatorMetropolitanId } from 'state/dashboard';
import { NEW_GROUP_ID } from 'utils/constants';
import { Region } from 'utils/constants/location';
import { groupRawToGroup } from 'utils/transforms';
import type { GroupRawResponse } from 'types/misc';

/**
 * Clone settings from a pre-existing group to the current one.
 * Uses react-query with onSuccess so the fetched group is committed to state
 * only when the request succeeds, avoiding race conditions.
 *
 * @param cloneId Id of the group to clone
 * @param keys List of keys to clone, if none are provided then all keys will be cloned
 * @param isCloning Used to debounce group cloning - if true, fetch and apply cloned group
 */
export const useDashboardDuplicate = (cloneId: string, isCloning: boolean = false) => {
  const [, setGroup] = useAtom(groupAtom);
  const { updateSelectedCompanies } = useCompanies();
  const [, setMercatorMetropolitanId] = useMercatorMetropolitanId();

  const enabled = isCloning && !!cloneId && cloneId !== NEW_GROUP_ID;

  useQuery(
    ['groupForClone', cloneId],
    () => fetchGroup(cloneId) as Promise<GroupRawResponse | undefined>,
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      onSuccess: (groupRaw) => {
        if (!groupRaw) return;

        const convertedGroup = groupRawToGroup(groupRaw);

        setGroup({ ...convertedGroup, id: NEW_GROUP_ID });

        if (groupRaw.companies) {
          updateSelectedCompanies(groupRaw.companies);
        }
        setMercatorMetropolitanId(groupRaw.mercator_metropolitan_id as Region);
      },
    },
  );
};
